import React from 'react';
import { Paper, Box, TextField, MenuItem, Typography, Button } from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
  GridOn as GridOnIcon,
} from '@mui/icons-material';
import { Provider, Region } from '../../types';
import { Totals } from './types';
import { formatCurrency } from '../../utils/formatCurrency';

interface QuoteHeaderProps {
  providers: Provider[];
  regions: Region[];
  selectedProvider: number | string;
  selectedRegion: number | string;
  quoteName: string;
  totals: Totals;
  onProviderChange: (value: string) => void;
  onRegionChange: (value: string) => void;
  onQuoteNameChange: (value: string) => void;
  onSave: () => void;
  onExportPDF: () => void;
  onExportExcel: () => void;
  onAddVM: () => void;
  onAddDatabase: () => void;
  onAddOtherItem: () => void;
  onAddPowerVM?: () => void;
  onAddPowerAddon?: () => void;
  showAddButtons: boolean;
  isIBM?: boolean;
}

const QuoteHeader: React.FC<QuoteHeaderProps> = ({
  providers, regions, selectedProvider, selectedRegion, quoteName, totals,
  onProviderChange, onRegionChange, onQuoteNameChange,
  onSave, onExportPDF, onExportExcel,
  onAddVM, onAddDatabase, onAddOtherItem, onAddPowerVM, onAddPowerAddon, showAddButtons, isIBM,
}) => {
  return (
    <>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            select label="Provider" value={selectedProvider}
            onChange={(e) => onProviderChange(e.target.value)}
            sx={{ minWidth: 230, maxWidth: 240 }}
          >
            {providers.map((p) => <MenuItem key={p.ProviderId} value={p.ProviderId}>{p.ProviderName}</MenuItem>)}
          </TextField>

          <TextField
            select label="Region" value={selectedRegion}
            onChange={(e) => onRegionChange(e.target.value)}
            disabled={!selectedProvider || regions.length === 0}
            sx={{ minWidth: 230, maxWidth: 240 }}
          >
            {regions.map((r) => <MenuItem key={r.RegionId} value={r.RegionId}>{r.RegionName}</MenuItem>)}
          </TextField>

          <TextField
            label="Quote Name" value={quoteName}
            onChange={(e) => onQuoteNameChange(e.target.value)}
            sx={{ minWidth: 300, maxWidth: 300 }}
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ flexGrow: 1 }} />

          {/* Totals box */}
          <Box
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
              minWidth: 200, px: 2, py: 0.5, backgroundColor: 'primary.main', color: 'primary.contrastText', borderRadius: 1,
            }}
          >
            <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 }}>
              {formatCurrency(totals.monthly)}
              <Typography component="span" variant="caption" sx={{ fontSize: '0.75rem', ml: 0.5, opacity: 0.9 }}> / per month</Typography>
            </Typography>
            <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2 }}>
              {formatCurrency(totals.annual)}
              <Typography component="span" variant="caption" sx={{ fontSize: '0.75rem', ml: 0.5, opacity: 0.9 }}> / per year</Typography>
            </Typography>
            {totals.oneTime > 0 && (
              <Typography variant="h5" sx={{ fontSize: '1.0rem', fontWeight: 700, lineHeight: 1.4, opacity: 0.9 }}>
                {formatCurrency(totals.oneTime)}
                <Typography component="span" variant="caption" sx={{ fontSize: '0.75rem', ml: 0.5, opacity: 0.9 }}> one-time build</Typography>
              </Typography>
            )}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={onSave}>
            Save Quote
          </Button>
          <Button variant="outlined" color="primary" startIcon={<PdfIcon />} onClick={onExportPDF}>
            PDF
          </Button>
          <Button variant="outlined" color="success" startIcon={<GridOnIcon />} onClick={onExportExcel}>
            Excel
          </Button>
        </Box>
      </Paper>

      {showAddButtons && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={onAddVM}>Add VM</Button>
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={onAddDatabase}>Add Database</Button>
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={onAddOtherItem}>Add Other</Button>
          {isIBM && onAddPowerVM && (
            <Button variant="contained" color="info" startIcon={<AddIcon />} onClick={onAddPowerVM}>Add IBM Power VM</Button>
          )}
          {isIBM && onAddPowerAddon && (
            <Button variant="contained" color="info" startIcon={<AddIcon />} onClick={onAddPowerAddon}>Add IBM Power Addon</Button>
          )}
        </Box>
      )}
    </>
  );
};

export default QuoteHeader;
